<template>
    <Accordion v-if="useRouteNavigation"
               ref="accordionRef"
               :header-is-button-tag="false"
               header-class="text-12 font-medium w-full"
               icon=""
               tag="div"
               :expanded="startOpened">
        <template #header>
            <CultureAwareRouterLink :to="{ path: menuItem.url, query: $route.query }"
                                    class="navigation-link w-full font-medium"
                                    :class="{'active' : isActive || startOpened, 'first': index === 0, 'last': last || startOpened }">
                <CategoryNameAndCount :menu-item="menuItem"/>
            </CultureAwareRouterLink>
        </template>
        <transition-group v-if="hasChildren(menuItem)"
                          tag="ul"
                          enter-active-class="animated fadeIn u-anim-dur-250"
                          leave-active-class="animated fadeOut u-anim-dur-150">
            <li v-for="(child, i) in getChildren(menuItem)" :key="child.id"
                class="navigation-link-container"
                :class="{ 'active': hasChildren(child) && isMenuActive(child) }">
                <NavigationTree :menu-item="child"
                                :index="index + 1"
                                :last="i === getChildren(menuItem).length - 1"
                                :selected-menu-item="selectedMenuItem"
                                :use-route-navigation="useRouteNavigation"
                                @menuChanged="selectMenuItem"/>
            </li>
        </transition-group>
    </Accordion>
    <ReadMore v-else
              ref="readMoreRef"
              :min-items="readMoreMinItems"
              use-observer
              :expanded="true"
              @click="trackReadMoreExpandCollapse">
        <div v-for="(child, i) in getChildren(menuItem)" :key="child.id"
             class="navigation-link-container text-12"
             :class="{ 'active': hasChildren(child) && isMenuActive(child) }">
            <CultureAwareRouterLink :to="{ path: child.url }"
                                    class="navigation-link w-full"
                                    :class="{'active' : isMenuActive(child), 'last': i === getChildren(menuItem).length - 1 }">
                <CategoryNameAndCount :menu-item="child"/>
            </CultureAwareRouterLink>
        </div>
        <template #collapse>
            <div class="text-left">
                <span class="text-12 theme-text-linkcolor">
                    {{ $dictionary.get('Generic.ShowLess') }}
                </span>
            </div>
        </template>
        <template #expand="{ expandWillShowNMore }">
            <div
                class="text-12 ml-15 text-left relative w-full">
                <div class="navigation-link last w-full theme-text-linkcolor">
                    {{ $dictionary.get('Search.Facets.Categories.ShowMoreCategories', [expandWillShowNMore]) }}
                </div>
            </div>
        </template>
    </ReadMore>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, Ref } from 'vue';
import { MenuItem } from '@/types/apiServerContract';
import { NavigationItem } from '@/types/contentServerContract';
import type { ExtendedMenuItem } from '@/project/layout/page-header/mobile/mobileMenu.types';
import CategoryNameAndCount from '@/project/search/navigation/CategoryNameAndCount.vue';
import GTMBasics from '@/project/tracking/gtm/GTM.basics';
import Accordion from '@/core/ui-components/Accordion.vue';
type AccordionType = InstanceType<typeof Accordion>;
type TreeMenuItem = MenuItem | NavigationItem | ExtendedMenuItem;

interface NavigationTreeProps {
    menuItem: TreeMenuItem;
    selectedMenuItem?: TreeMenuItem | null;
    useRouteNavigation?: boolean;
    index?: number;
    last?: boolean;
    startOpen?: boolean;
}
const props = withDefaults(defineProps<NavigationTreeProps>(), {
    selectedMenuItem: null,
    useRouteNavigation: false,
    index: 0,
    last: false,
    startOpen: false
});

const emit = defineEmits<{
    (event: 'menuChanged', menuItem: TreeMenuItem): void
}>();

const startOpened = computed((): boolean => {
    return hasChildren(props.menuItem) && (isActive.value || isMenuAChildOfMe(props.menuItem, props.selectedMenuItem?.url));
});
const accordionRef: Ref<AccordionType | null> = ref(null);
// @ts-ignore
const readMoreRef: Ref<ReadMore | null> = ref(null);

const isActive = computed(() => props.menuItem.id === props.selectedMenuItem?.id);

function getChildren(menuItem: TreeMenuItem): TreeMenuItem[] {
    return (menuItem.children ?? []) as TreeMenuItem[];
}

function hasChildren(menuItem: TreeMenuItem): boolean {
    return getChildren(menuItem).length > 0;
}

function isMenuActive(menuItem: TreeMenuItem) {
    return menuItem.id === props.selectedMenuItem?.id || isMenuAChildOfMe(menuItem, props.selectedMenuItem?.url);
}

function validateReadyState() {
    if (accordionRef.value) {
        if (startOpened.value) {
            accordionRef.value.open();
        } else {
            accordionRef.value.close()
        }
    }
}

function isMenuAChildOfMe(menuItem: TreeMenuItem, url?: string): boolean {
    const children = getChildren(menuItem);
    if (!url || children.length === 0) return false;

    for (const menu of children) {
        if (menu.url === url) {
            return true;
        }

        if (hasChildren(menu)) {
            const found = isMenuAChildOfMe(menu, url);
            if (found) {
                return found;
            }
        }
    }

    return false;
}

const readMoreMinItems = computed(() => {
    const orphan = 3;
    let minFacetCount = 5;
    const children = getChildren(props.menuItem);
    if (children.length < minFacetCount) {
        minFacetCount = children.length;
    }
    if (children.length < minFacetCount + orphan) {
        minFacetCount = minFacetCount + orphan;
    }
    let lastSelectedMenuItem = 0;
    children.forEach((menuItem, index) => {
        if (isMenuActive(menuItem)) {
            lastSelectedMenuItem = index;
        }
    });
    return Math.max(minFacetCount, lastSelectedMenuItem + 1);
});

function trackReadMoreExpandCollapse(mode: string) {
    GTMBasics.pushToDataLayer({
        event: 'facets.' + mode,
        facetCategory: 'category-name'
    });
}

function selectMenuItem(menuItem: TreeMenuItem) {
    emit('menuChanged', menuItem);
}

watch(() => props.selectedMenuItem, () => {
    validateReadyState();
});

watch(() => props.menuItem, () => {
    if (readMoreRef.value) {
        readMoreRef.value.externalEventHappenedReconsiderMode(true);
    }
});

onMounted(() => {
    validateReadyState();
});
</script>
